import { useState, useCallback } from 'react';
import { useSubscriptionContext } from '../../../contexts/SubscriptionContext';
import { useAPI } from '../../../hooks/useAPI';
import { WhatsAppConfigService } from '../services/whatsappConfigService';
import type { WhatsAppConfigFormData } from '../types/whatsapp.types';

export const useWhatsAppConfig = () => {
  const { currentSubscription } = useSubscriptionContext();
  
  // The backend owns the final permission check. Keeping this read independent
  // from a UI plan label avoids hiding an existing tenant configuration.
  const configQuery = useAPI(
    currentSubscription ? `/whatsapp/api/config/${currentSubscription.id}/` : null
  );

  return {
    data: configQuery.data,
    isLoading: configQuery.loading,
    error: configQuery.error,
    refetch: configQuery.refetch,
  };
};

export const useWhatsAppConfigStatus = (configId?: number) => {
  const statusQuery = useAPI(
    configId ? `/whatsapp/api/config/${configId}/status/` : null
  );

  return {
    data: statusQuery.data,
    isLoading: statusQuery.loading,
    error: statusQuery.error,
    refetch: statusQuery.refetch,
  };
};

export const useCreateWhatsAppConfig = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutateAsync = useCallback(async (data: WhatsAppConfigFormData) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await WhatsAppConfigService.createConfig(data);
      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erreur lors de la création';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    mutateAsync,
    isLoading,
    error,
  };
};

export const useUpdateWhatsAppConfig = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutateAsync = useCallback(async ({ id, data }: { id: number; data: Partial<WhatsAppConfigFormData> }) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await WhatsAppConfigService.updateConfig(id, data);
      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erreur lors de la mise à jour';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    mutateAsync,
    isLoading,
    error,
  };
};

export const useValidateMetaTokens = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutateAsync = useCallback(async (configId: number) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await WhatsAppConfigService.validateMetaTokens(configId);
      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erreur lors de la validation';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    mutateAsync,
    isLoading,
    error,
  };
};

export const useSendTestMessage = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const mutateAsync = useCallback(async ({ configId, testData }: { 
    configId: number; 
    testData: { to_number: string; message: string } 
  }) => {
    setIsLoading(true);
    setError(null);
    
    try {
      const result = await WhatsAppConfigService.sendTestMessage(configId, testData);
      return result;
    } catch (err: any) {
      const errorMessage = err.response?.data?.message || 'Erreur lors de l\'envoi';
      setError(errorMessage);
      throw err;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return {
    mutateAsync,
    isLoading,
    error,
  };
};
